'use client'

import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { GATrackingProvider } from './ga-tracking-provider'

/* -------- Dynamic imports -------- */
const Footer = dynamic(() =>
  import('@/components/layout/footer').then((m) => m.Footer)
)
const Header = dynamic(() =>
  import('@/components/layout/header').then((m) => m.Header)
)
const BackToTop = dynamic(() =>
  import('@/components/ui/back-to-top').then((m) => m.BackToTop)
)
const ContactFab = dynamic(() =>
  import('@/components/ui/contact-fab').then((m) => m.ContactFab)
)
const StickySetting = dynamic(
  () =>
    import('@/components/layout/sticky-setting').then((m) => m.StickySetting),
  { ssr: false }
)
const StickyCoolClub = dynamic(
  () =>
    import('@/components/layout/sticky-coolclub').then((m) => m.StickyCoolClub),
  { ssr: false }
)

/* -------- Helpers -------- */
const startsWith = (patterns: string[], path: string) =>
  patterns.some((p) => path.startsWith(p))

/* -------- Path rules -------- */
const NO_LAYOUT = ['/wireframe', '/doi-tra']
const NO_FOOTER = ['/checkout', '/cart', '/doi-tra']
const NO_STICKY = ['/cart', '/doi-tra']
const NO_TOPBAR = ['/cart', '/doi-tra']
const NO_NAVBAR = ['/doi-tra'] // keep if you still need it
const NO_PROMO = ['/cart', '/doi-tra']
const NO_GA = ['/auth/callback']

export const LayoutProvider = ({
  children,
  isMobile,
}: {
  children: React.ReactNode
  isMobile: boolean
}) => {
  const pathname = usePathname()

  /* -------- Visibility flags -------- */
  const showLayout = !startsWith(NO_LAYOUT, pathname)
  const showFooter = !startsWith(NO_FOOTER, pathname)
  const showSticky = !startsWith(NO_STICKY, pathname)
  const showTopBar = !startsWith(NO_TOPBAR, pathname)
  const showNavbar = !startsWith(NO_NAVBAR, pathname)
  const showPromo = !startsWith(NO_PROMO, pathname)
  const showGA = !startsWith(NO_GA, pathname)

  if (!showLayout) return <>{children}</>

  return (
    <GATrackingProvider shouldShowGATracking={showGA}>
      <Header
        isShowTopBar={showTopBar}
        isShowNavbar={showNavbar}
        isShowPromotion={showPromo}
        isMobile={isMobile}
      />
      <main className='min-h-screen'>{children}</main>

      {showFooter && <Footer />}
      {showSticky && (
        <>
          <BackToTop />
          <ContactFab />
        </>
      )}

      {/* always render these two (client-only) sticky helpers */}
      <StickySetting />
      <StickyCoolClub />
    </GATrackingProvider>
  )
}
